import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import type { UserSelect } from '@org/model';

type AuthenticatedUser = Pick<UserSelect, 'userId' | 'email' | 'firstName' | 'lastName'> & {
  picture?: string;
  verified_email?: boolean;
  permissions: string[];
};

interface AuthContextType {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  login: () => Promise<void>;
  loginSilent: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  refreshAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const isE2eAuthBypassEnabled = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';
const DEFAULT_AUTH0_SCOPES = ['openid', 'profile', 'email', 'offline_access'];
const DEFAULT_API_AUDIENCE = 'https://dev.api.english2empower.com';

const normalizeEnv = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.toUpperCase() === 'TO_BE_FILLED') {
    return undefined;
  }

  return trimmed;
};

const buildScope = (value?: string): string => {
  const parts = value
    ?.split(/[,\s]+/)
    .map((scope) => scope.trim())
    .filter(Boolean);

  const scopes = new Set(DEFAULT_AUTH0_SCOPES);

  if (parts?.length) {
    for (const scope of parts) {
      scopes.add(scope);
    }
  }

  return Array.from(scopes).join(' ');
};

const mapUserInfoToModel = (info: any): AuthenticatedUser => ({
  userId: info?.sub ?? info?.user_id ?? '',
  email: info?.email ?? '',
  firstName: info?.given_name ?? info?.name ?? '',
  lastName: info?.family_name ?? '',
  picture: info?.picture,
  verified_email: info?.email_verified ?? info?.verified_email,
  permissions: info?.permissions ?? [],
});

const createBypassUser = (): AuthenticatedUser =>
  mapUserInfoToModel({
    sub: 'e2e-user',
    email: 'paulogtabarro@gmail.com',
    given_name: 'E2E',
    family_name: 'Tester',
    name: 'E2E Tester',
    picture: 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=identicon',
    email_verified: true,
    permissions: ['admin:all'],
  });

interface AuthStateProviderProps {
  children: ReactNode;
  audience?: string;
  logoutReturnTo?: string;
  scope: string;
}

const AuthStateProvider: React.FC<AuthStateProviderProps> = ({ children, audience, logoutReturnTo, scope }) => {
  const { user, isLoading, isAuthenticated, loginWithRedirect, logout: auth0Logout, getAccessTokenSilently } =
    useAuth0();

  const baseTokenOptions = useMemo(() => {
    if (audience || scope) {
      return {
        authorizationParams: {
          ...(audience ? { audience } : {}),
          scope,
        },
      };
    }

    return undefined;
  }, [audience, scope]);

  const login = useCallback(async () => {
    await loginWithRedirect();
  }, [loginWithRedirect]);

  // Silent login - redirects through Auth0 without showing login UI
  // Used to get fresh tokens after role changes
  const loginSilent = useCallback(async () => {
    await loginWithRedirect({
      authorizationParams: {
        prompt: 'none',
        ...(audience ? { audience } : {}),
        scope,
      },
    });
  }, [loginWithRedirect, audience, scope]);

  const logout = useCallback(async () => {
    const returnTo = logoutReturnTo ?? (typeof window !== 'undefined' ? window.location.origin : undefined);
    auth0Logout({ logoutParams: { returnTo } });
  }, [auth0Logout, logoutReturnTo]);

  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently(baseTokenOptions);
    } catch (error) {
      const auth0Error = error as { error?: string; message?: string; error_description?: string };
      const message = `${auth0Error?.error ?? ''} ${auth0Error?.message ?? ''} ${
        auth0Error?.error_description ?? ''
      }`.toLowerCase();

      const needsReAuth =
        message.includes('login_required') ||
        message.includes('consent_required') ||
        message.includes('missing_refresh_token') ||
        message.includes('invalid_grant') ||
        message.includes('session_missing');

      if (needsReAuth) {
        await loginWithRedirect({
          authorizationParams: {
            ...(audience ? { audience } : {}),
            scope,
          },
        });
      }

      throw error;
    }
  }, [baseTokenOptions, getAccessTokenSilently, loginWithRedirect]);

  const refreshAccessToken = useCallback(async () => {
    const freshToken = await getAccessTokenSilently({
      ...(baseTokenOptions ?? {}),
      cacheMode: 'off',
      detailedResponse: true,
    });

    // Decode and log the token payload to see permissions
    try {
      const payload = JSON.parse(atob(freshToken.access_token.split('.')[1]));
    } catch (e) {
      console.warn('[Auth] Could not decode token payload');
    }

    // Warm the cache so subsequent calls without cacheMode reuse the new token
    await getAccessTokenSilently(baseTokenOptions);

    return freshToken.access_token;
  }, [baseTokenOptions, getAccessTokenSilently]);

  const value: AuthContextType = useMemo(() => ({
    user: user ? mapUserInfoToModel(user) : null,
    isLoading,
    isLoggedIn: isAuthenticated,
    login,
    loginSilent,
    logout,
    getAccessToken,
    refreshAccessToken,
  }), [user, isLoading, isAuthenticated, login, loginSilent, logout, getAccessToken, refreshAccessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const BypassAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const value = useMemo<AuthContextType>(() => ({
    user: createBypassUser(),
    isLoading: false,
    isLoggedIn: true,
    login: () => Promise.resolve(),
    loginSilent: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    getAccessToken: () => Promise.resolve('e2e-access-token'),
    refreshAccessToken: () => Promise.resolve('e2e-access-token'),
  }), []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  if (isE2eAuthBypassEnabled) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>;
  }

  const domain = normalizeEnv(import.meta.env.VITE_AUTH0_DOMAIN);
  const clientId = normalizeEnv(import.meta.env.VITE_AUTH0_CLIENT_ID);
  const audience = normalizeEnv(import.meta.env.VITE_AUTH0_AUDIENCE) ?? DEFAULT_API_AUDIENCE;
  const redirectUri =
    normalizeEnv(import.meta.env.VITE_AUTH0_REDIRECT_URI) ??
    (typeof window !== 'undefined' ? window.location.origin : undefined);
  const logoutReturnTo = normalizeEnv(import.meta.env.VITE_AUTH0_LOGOUT_URI) ?? redirectUri;
  const scope = buildScope(normalizeEnv(import.meta.env.VITE_AUTH0_SCOPE));

  if (!domain || !clientId) {
    console.error('VITE_AUTH0_DOMAIN or VITE_AUTH0_CLIENT_ID is not set in environment variables');
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Configuration Error</h2>
        <p>Auth0 is not properly configured. Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.</p>
      </div>
    );
  }

  const authorizationParams: Record<string, string> = {
    scope,
  };

  if (redirectUri) {
    authorizationParams.redirect_uri = redirectUri;
  }

  if (audience) {
    authorizationParams.audience = audience;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback
    >
      <AuthStateProvider audience={audience} logoutReturnTo={logoutReturnTo} scope={scope}>
        {children}
      </AuthStateProvider>
    </Auth0Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
